import { useEffect, useState, Fragment, useMemo } from "react";
import { UpdateAccountMetadata } from "@/Utils/Promises";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Icon,
  Modal
} from "@theorgchartdev/storybook";
import cx from "classnames";
import { GetNewProfile } from "@/appComponents/Blocks/Modals/SettingsModal/AccountSettings/Security/Security";
import { ConditionalDefinitionModal } from "./Components/ConditionalDefinitionModal";
import {
  SetRowSecProfileToJSON,
  getProfileNameError
} from "@/appComponents/Blocks/Modals/SettingsModal/AccountSettings/Security/SecurityUtils";
import { appstr } from "@coreapp/utils/appstr";
import { QAlert } from "@coreapp/CoreApp/QAlert";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { GetAllFieldDefs } from "@/Utils/DataLib";
import {
  GetAvailableFields,
  enumRuleSets,
  disabledEmployeeFields,
  disabledPositionFields,
  disabledHolderFields,
  getReselectionAfterDelete
} from "./RowLevelSecurityModal.utils";
import { NameSection } from "./Components/NameSection";
import { RuleOptionsSection } from "./Components/RuleOptionsSection";
import { RuleCriteriaSection } from "./Components/RuleCriteriaSection";
import { FieldsSection } from "./Components/FieldsSection";
import { enumObjClassNames } from "@coreapp/constants";
import { createCompositeFieldId } from "@/appComponents/Blocks/Menus/FieldToggleMenu/MultiClassFieldToggleMenu";

export const RowLevelSecurityModal = ({
  show,
  existingProfileID,
  accountConfig,
  profiles,
  onClose,
  isCreateNewProfile
}) => {
  const [editProfile, setEditProfile] = useState(null);
  const [clonedProfile, setClonedProfile] = useState(null);
  const [isNewProfile, setIsNewProfile] = useState(isCreateNewProfile);
  const [changedFields, setChangedFields] = useState([]);
  const [errorStatus, setErrorStatus] = useState("");
  const [selectedRule, setSelectedRule] = useState(enumRuleSets.allowed);
  const [selectedConditionalRule, setSelectedConditionalRule] = useState(null);
  const [activeRule, setActiveRule] = useState(null);

  const { showPricingAndPackaging } = usePermissionCheck();

  useEffect(() => {
    InitializeProfile();
  }, []);

  const doc = GetDoc();
  const isPositions = doc && doc.IsPositionDocument();

  const allFieldDefs = useMemo(() => {
    return GetAllFieldDefs(true, false, true, true);
  }, []);

  const allFieldDefsArray = useMemo(
    () => [...(allFieldDefs.Box || []), ...(allFieldDefs.Position || [])],
    [allFieldDefs]
  );

  const updateProfile = async data => {
    if (!data) return;
    const { result, success } = await UpdateAccountMetadata(data);
    if (result && success) {
      setClonedProfile(JSON.parse(JSON.stringify(editProfile)));
      clearDirty();
      onClose(editProfile, existingProfileID);
    } else {
      var w = new QAlert();
      w.InitAlert(appstr("Alert"), appstr("groupupdateerr"), null, GetWSMgr(), true);
    }
  };

  const InitializeProfile = () => {
    let initProfile = null;
    if (existingProfileID != null && profiles[existingProfileID] && !isNewProfile) {
      initProfile = profiles[existingProfileID];
    } else if (isNewProfile) {
      let { newProfile } = GetNewProfile(accountConfig["security"], "rowlevel");
      initProfile = newProfile;
      setIsNewProfile(true);
    }
    if (initProfile != null) {
      setClonedProfile(JSON.parse(JSON.stringify(initProfile)));
      let initializedEditProfile = InitializeSecurityProfile(
        JSON.parse(JSON.stringify(initProfile))
      );
      setEditProfile(initializedEditProfile);
    }
  };

  const GetDisabledCoreFields = isPositions => {
    const employeeFields = allFieldDefs.Box;
    if (isPositions) {
      //use composite ID for positions
      const positionFields = allFieldDefs.Position;
      let disabledCoreFields = [];

      disabledPositionFields.map(fieldID => {
        let field = positionFields.find(f => f.GetFieldID() === fieldID);
        if (!field) field = employeeFields.find(f => f.GetFieldID() === fieldID);
        if (!field) return;
        disabledCoreFields.push(
          createCompositeFieldId(field.GetFieldName(), enumObjClassNames.position)
        );
      });

      disabledHolderFields.map(fieldID => {
        let field = positionFields.find(f => f.GetFieldID() === fieldID);
        if (!field) field = employeeFields.find(f => f.GetFieldID() === fieldID);
        if (!field) return;
        disabledCoreFields.push(
          createCompositeFieldId(field.GetFieldName(), enumObjClassNames.box)
        );
      });

      return disabledCoreFields;
    } else {
      return disabledEmployeeFields.map(fieldID => {
        const field = employeeFields.find(f => f.GetFieldID() === fieldID);
        if (!field) return;
        return field.GetFieldName();
      });
    }
  };

  const InitializeSecurityProfile = profile => {
    let g = profile ? profile : editProfile;
    if (!g["allowedFields"]) g["allowedFields"] = [];
    if (!g["pos_allowedFields"]) g["pos_allowedFields"] = [];
    if (!g["restrictedFields"]) g["restrictedFields"] = [];
    if (!g["pos_restrictedFields"]) g["pos_restrictedFields"] = [];
    if (!g["conditionalFields"]) g["conditionalFields"] = [];
    return g;
  };

  const handleChange = (key, value) => {
    setEditProfile({
      ...editProfile,
      [key]: value
    });
    ChangedField(key);
  };

  const ChangedField = key => {
    if (!changedFields.includes(key)) setChangedFields([...changedFields, key]);
  };

  const handleInputChange = e => {
    const value = e.target.value;
    const key = e.target.name;
    if (key === "name" && errorStatus) setErrorStatus("");
    handleChange(key, value);
  };

  const checkDirty = () => {
    return changedFields?.length > 0;
  };

  const clearDirty = () => {
    setChangedFields([]);
  };

  const checkName = () => {
    return getProfileNameError(
      accountConfig["security"],
      editProfile.name,
      "rowlevel",
      existingProfileID
    );
  };

  const onSaveProfile = () => {
    let nameMsg = checkName();
    if (nameMsg.length > 0) {
      setErrorStatus(nameMsg);
      return;
    }
    setErrorStatus("");
    let updatedSecurityProfiles = SetRowSecProfileToJSON(
      accountConfig,
      { ...editProfile, name: editProfile.name.trim() },
      existingProfileID
    );
    let curSecObj = accountConfig["security"];
    if (!curSecObj || !curSecObj["rowlevel"])
      var securityDump = { rowlevel: updatedSecurityProfiles };
    else {
      curSecObj["rowlevel"] = updatedSecurityProfiles;
      securityDump = curSecObj;
    }
    const theData = JSON.stringify({ security: securityDump });
    updateProfile(theData);
  };

  const onCancel = (skipDirtyCheck = false) => {
    const wsMgr = GetWSMgr();
    if (checkDirty() && !skipDirtyCheck) {
      wsMgr.CreateReactAlert(
        appstr("Save changes to") + editProfile["name"] + "?",
        appstr("All changes will be lost"),
        () => onSaveProfile(),
        () => onCancel(true),
        appstr("Save Changes"),
        appstr("Discard Changes")
      );
      return;
    }
    clearDirty();
    setEditProfile(clonedProfile);
    onClose(false);
  };

  const onConditionalRuleChange = (mode, conditionalIdx) => {
    let ruleCopy = GetFieldCopy(enumRuleSets.conditional);
    switch (mode) {
      case "remove": {
        ruleCopy.splice(conditionalIdx, 1);
        handleChange("conditionalFields", ruleCopy);
        const next = getReselectionAfterDelete(
          parseInt(conditionalIdx, 10),
          selectedConditionalRule,
          ruleCopy.length,
          selectedRule
        );
        setSelectedConditionalRule(next.selectedConditionalRule);
        if (next.selectedRule) setSelectedRule(next.selectedRule);
        break;
      }
      case "copy":
        let copyRule = ruleCopy[conditionalIdx];
        var theClonedRule = JSON.parse(JSON.stringify(copyRule));
        ruleCopy.push(theClonedRule);
        handleChange("conditionalFields", ruleCopy);
        break;
    }
  };
  const AddConditionalRule = () => {
    let conditionalRule = {};
    conditionalRule["rules"] = [];
    conditionalRule["fields"] = [];
    conditionalRule["pos_fields"] = [];

    let newConditions = GetFieldCopy(enumRuleSets.conditional);
    newConditions.push(conditionalRule);

    let ruleIndex = newConditions.length - 1;
    ruleIndex = ruleIndex.toString();
    setSelectedConditionalRule(ruleIndex);
    setSelectedRule(enumRuleSets.conditional);

    handleChange("conditionalFields", newConditions);
  };

  const changeRuleSet = (set, conditionalIndex) => {
    setSelectedRule(set);
    if (conditionalIndex != null);
    setSelectedConditionalRule(conditionalIndex);
  };

  const GetCurrentConditionalRuleFields = className => {
    let fields = [];
    if (editProfile["conditionalFields"]) {
      let conditionalRules = editProfile["conditionalFields"];
      let targetRuleField = conditionalRules[selectedConditionalRule];
      if (className === enumObjClassNames.position) {
        fields = targetRuleField["pos_fields"]
          ? JSON.parse(JSON.stringify(targetRuleField["pos_fields"]))
          : [];
      } else {
        fields = targetRuleField["fields"]
          ? JSON.parse(JSON.stringify(targetRuleField["fields"]))
          : [];
      }
    }
    return fields;
  };

  const GetFieldCopy = set => {
    return editProfile ? JSON.parse(JSON.stringify(editProfile[set])) : [];
  };

  const ToggleField = target => {
    const targetField = target.value;
    const targetClass = target.className ?? enumObjClassNames.box;
    if (!targetField) return;
    switch (selectedRule) {
      case enumRuleSets.conditional:
        let conditionalRules = GetFieldCopy(enumRuleSets.conditional);
        let conditionalRule = conditionalRules[selectedConditionalRule];
        if (!conditionalRule) return;

        let activeRuleFields = GetCurrentConditionalRuleFields(targetClass);
        let condFieldIdx = activeRuleFields.indexOf(targetField);
        if (condFieldIdx > -1) activeRuleFields.splice(condFieldIdx, 1);
        else activeRuleFields.push(targetField);

        const fieldKey = targetClass === enumObjClassNames.position ? "pos_fields" : "fields";
        conditionalRule[fieldKey] = activeRuleFields;
        conditionalRules[selectedConditionalRule] = conditionalRule;
        handleChange(selectedRule, conditionalRules);
        break;
      case enumRuleSets.allowed:
      case enumRuleSets.restricted:
        let activeFieldsSet = selectedRule;
        if (targetClass === enumObjClassNames.position) {
          activeFieldsSet = `pos_${selectedRule}`;
        }
        let activeFields = GetFieldCopy(activeFieldsSet);
        let targetIdx = activeFields.indexOf(targetField);
        if (targetIdx > -1) activeFields.splice(targetIdx, 1);
        else activeFields.push(targetField);
        handleChange(activeFieldsSet, activeFields);
        break;
    }
  };

  const HandleResetFields = () => {
    if (selectedRule === enumRuleSets.conditional) {
      const conditionalFields = GetFieldCopy(enumRuleSets.conditional);
      conditionalFields[selectedConditionalRule] = {
        ...conditionalFields[selectedConditionalRule],
        fields: [],
        pos_fields: []
      };
      handleChange("conditionalFields", conditionalFields);
    } else {
      handleChange(selectedRule, []);
      handleChange(`pos_${selectedRule}`, []);
    }
  };

  const openDefinitionEditor = (rule = { definition: {}, properties: "$eq" }, index = -1) => {
    if (selectedRule !== enumRuleSets.conditional) return;
    setActiveRule({ index: index, rule: rule });
  };

  const onCloseDefinitionEditor = () => {
    setActiveRule(null);
  };

  const onSaveDefinition = (newRule, index) => {
    let conditionalRules = GetFieldCopy(enumRuleSets.conditional);
    let conditionalRule = conditionalRules[selectedConditionalRule];
    var ruleData = conditionalRule["rules"];
    var curRule = ruleData[index];

    if (curRule) conditionalRule["rules"][index] = newRule;
    else conditionalRule["rules"].push(newRule);

    conditionalRules[selectedConditionalRule] = conditionalRule;
    handleChange("conditionalFields", conditionalRules);
    onCloseDefinitionEditor();
  };

  const handleDefinitionUpdate = (type, index) => {
    let conditionalRules = GetFieldCopy(enumRuleSets.conditional);
    let conditionalRule = conditionalRules[selectedConditionalRule];
    let rule = conditionalRule["rules"][index];
    let cloneRule = rule ? JSON.parse(JSON.stringify(rule)) : null;
    if (!cloneRule) return;
    switch (type) {
      case "edit":
        openDefinitionEditor(cloneRule, index);
        return;
      case "copy":
        conditionalRule["rules"].push(cloneRule);
        break;
      case "remove":
        conditionalRule["rules"].splice(index, 1);
        break;
    }
    conditionalRules[selectedConditionalRule] = conditionalRule;
    handleChange("conditionalFields", conditionalRules);
  };
  const GetSelectedFields = () => {
    if (selectedRule === enumRuleSets.conditional) {
      const conditionalFields = editProfile?.conditionalFields;
      return {
        [enumObjClassNames.box]: conditionalFields?.[selectedConditionalRule]?.fields ?? [],
        [enumObjClassNames.position]: conditionalFields?.[selectedConditionalRule]?.pos_fields ?? []
      };
    }
    const boxKey = selectedRule === enumRuleSets.allowed ? "allowedFields" : "restrictedFields";
    const posKey =
      selectedRule === enumRuleSets.allowed ? "pos_allowedFields" : "pos_restrictedFields";
    return {
      [enumObjClassNames.box]: editProfile?.[boxKey] ?? [],
      [enumObjClassNames.position]: editProfile?.[posKey] ?? []
    };
  };

  const headers = GetAvailableFields();
  const selectedFields = GetSelectedFields();

  const hasRuleDefinitions = () => {
    let conditionalRules = GetFieldCopy(enumRuleSets.conditional);
    let conditionalRule = conditionalRules[selectedConditionalRule];
    if (!conditionalRule) return false;
    return conditionalRule?.rules?.length > 0;
  };

  const currentConditionHasRules = hasRuleDefinitions();
  const currentRuleHasFields = GetFieldCopy(selectedRule).length > 0;

  const contentClass = "oc-modal-content";

  return (
    <Fragment>
      <CentralizedModalContainer show={show}>
        <Modal
          id="example-modal"
          show={show}
          centerScreen={true}
          fullScreen={true}
          className={"oc-modal-full-screen"}
        >
          <DialogTitle
            divider={true}
            onClose={() => onCancel()}
            data-testid="account-settings-security-rls-modal-close-btn"
          >
            <span className={"ocn-explorer-icon-label"}>
              <Icon size={"medium"} name={isNewProfile ? "user-plus" : "user-pen"} />
              {isNewProfile ? appstr("Create security profile") : appstr("Edit security profile")}
            </span>
          </DialogTitle>
          <DialogContent customContentClass={"oc-modal-content-container"}>
            <Flex
              gap={"24px"}
              overflow={"hidden"}
              direction={"column"}
              padding={"24px 16px"}
              height={"100%"}
            >
              <NameSection
                nameValue={editProfile?.name || ""}
                descriptionValue={editProfile?.description || ""}
                errorStatus={errorStatus}
                handleInputChange={handleInputChange}
              />
              <Flex
                gap={"8px"}
                className={contentClass}
                overflow={"hidden"}
                flex={"1"}
                justifyContent={"space-between"}
              >
                <Flex gap={"24px"} width={"33%"} height={"100%"}>
                  <RuleOptionsSection
                    selectedRule={selectedRule}
                    selectedConditionalRule={selectedConditionalRule}
                    conditionalFields={GetFieldCopy(enumRuleSets.conditional)}
                    changeRuleSet={changeRuleSet}
                    AddConditionalRule={AddConditionalRule}
                    onConditionalRuleChange={onConditionalRuleChange}
                    showPricingAndPackaging={showPricingAndPackaging}
                  />
                </Flex>
                <Flex gap={"24px"} width={"33%"} height={"100%"}>
                  <RuleCriteriaSection
                    selectedRule={selectedRule}
                    selectedConditionalRule={selectedConditionalRule}
                    currentConditionHasRules={currentConditionHasRules}
                    currentRuleHasFields={currentRuleHasFields}
                    conditionalRules={GetFieldCopy(enumRuleSets.conditional)}
                    openDefinitionEditor={openDefinitionEditor}
                    handleDefinitionUpdate={handleDefinitionUpdate}
                  />
                </Flex>
                <Flex gap={"24px"} width={"33%"} height={"100%"}>
                  <FieldsSection
                    editProfile={editProfile}
                    selectedFields={selectedFields}
                    ToggleField={ToggleField}
                    isPositions={isPositions}
                    headers={headers}
                    fieldDefs={allFieldDefsArray}
                    disabledFields={GetDisabledCoreFields(isPositions)}
                    currentRuleSet={selectedRule}
                    HandleResetFields={HandleResetFields}
                  />
                </Flex>
              </Flex>
            </Flex>
          </DialogContent>
          <DialogActions divider={true}>
            <DialogActions.Tertiary
              onClick={() => onCancel()}
              dataTestId="account-settings-security-rls-modal-cancel-btn"
            >
              {appstr("Cancel")}
            </DialogActions.Tertiary>
            <DialogActions.Primary
              onClick={() => onSaveProfile()}
              dataTestId="rls-modal-action-save"
            >
              {isNewProfile ? appstr("Create") : appstr("Save")}
            </DialogActions.Primary>
          </DialogActions>
        </Modal>
      </CentralizedModalContainer>
      <ConditionalDefinitionModal
        ruleObj={activeRule?.rule}
        show={activeRule != null}
        onSave={onSaveDefinition}
        onClose={onCloseDefinitionEditor}
        index={activeRule?.index}
        fieldDefs={allFieldDefs}
      />
    </Fragment>
  );
};
